import React, { ChangeEvent, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isCancel } from 'axios';
import {
  Box,
  Button,
  IconButton,
  Icons,
  Text,
  TextField,
  useTheme,
  Avatar,
  Chip,
  Autocomplete,
  Link,
  AULA_LIGHT_ALERT_INFO_BG_COLOR,
  Alert,
  AULA_DARK_ALERT_INFO_BG_COLOR,
} from '@ublend-npm/aulaui-core';

import { usersSearch } from '@core/api/search';
import changeConversationName from '@core/conversations/operationsResponsive/changeConversationName.action';
import createConversation from '@core/conversations/operations/createConversation.action';
import { SelectedUser } from '@core/conversations/types';
import { getCurrentSpaceId } from '@core/selectors/space';
import { currentUserId } from '@core/selectors/user';
import { isInstructor, isInstructorInSpace } from '@core/selectors/classroom';
import { AulaDispatch, AulaState } from '@core/types/state';
import changeConversationParticipants from '@core/conversations/operationsResponsive/changeConversationParticipants.action';
import pushRoute from '@app/utils/routing';
import { DARK } from '../../../../core/constants/theme';
import { analyticsTrackEvent } from '@core/utils/analytics';
import { MESSAGE_CONVERSATION_CREATED } from '@core/constants/analytics';

type CreateConversationProps = {
  onClose?: () => void;
};

const TIMEOUT_MS = 500;

const CreateConversation = ({ onClose }: CreateConversationProps) => {
  const [searchText, setSearchText] = useState('');
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [loadingCreation, setLoadingCreation] = useState(false);
  const [options, setOptions] = useState<SelectedUser[] | null>([]);
  const timeoutId = useRef<NodeJS.Timeout | null>(null);
  const { palette } = useTheme();
  const isDarkMode = palette?.mode === DARK;

  const dispatch: AulaDispatch = useDispatch();
  const spaceId = useSelector(getCurrentSpaceId);
  const userId = useSelector(currentUserId);
  const userIsInstructor = useSelector(isInstructor);
  const isUserInstructorInSpace = useSelector(isInstructorInSpace);
  const conversationName = useSelector(
    (state: AulaState) => state.inboxv2.createConversationName
  );
  const conversationParticipants = useSelector(
    (state: AulaState) => state.inboxv2.createConversationParticipants
  );

  const handleConversationNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    dispatch(changeConversationName(e.target.value));
  };
  const handleConversationParticipantsChange = (users: SelectedUser[]) => {
    dispatch(changeConversationParticipants(users));
  };

  const clearCreateConversationForm = () => {
    dispatch(changeConversationName(''));
    dispatch(changeConversationParticipants([]));
  };

  const handleUserSearch = (value: string) => {
    if (timeoutId.current) clearTimeout(timeoutId.current);
    setOptions(null);
    timeoutId.current = setTimeout(async () => {
      setLoadingSearch(true);
      try {
        const results = await usersSearch({
          query: value,
          spaceId: null,
          includeEducatorInSpaces: false,
        });
        setOptions(results);
      } catch (error) {
        if (!isCancel(error)) {
          console.error(error);
        }
      }
      setLoadingSearch(false);
      timeoutId.current = null;
    }, TIMEOUT_MS);
  };

  const handleCloseOrPushToMessages = () => {
    clearCreateConversationForm();
    if (onClose) {
      onClose();
    } else {
      pushRoute(`dashboard/${spaceId}/messages`);
    }
  };

  // fetch query parameters from url
  const getQueryParamsStr = () => {
    const hash = window.location.hash;
    // Fetch the query parameters
    const queryParamsStr = hash?.includes('?') ? hash.split('?')[1] : '';
    return queryParamsStr;
  };

  // Append query parameters to existing route
  const appendParamsToRoute = (paramsStr: String, currentRoute: String) => {
    if (!paramsStr) {
      return currentRoute;
    }
    return `${currentRoute}?${paramsStr}`;
  };

  const handleCreateConversation = async () => {
    setLoadingCreation(true);
    const conversation = await dispatch(
      createConversation({
        spaceId,
        title: conversationName || '-',
        users: conversationParticipants.map((u) => ({
          ...u,
          objectId: u.objectId || u.id,
        })),
        educatorOnly: false,
      })
    );
    setLoadingCreation(false);
    if (conversation) {
      // Trigger analytic event when conversation is created
      analyticsTrackEvent(MESSAGE_CONVERSATION_CREATED, {
        other: {
          participantCount: conversationParticipants?.length || 0,
        },
      });
      clearCreateConversationForm();
      onClose?.();
      const queryParamsStr = getQueryParamsStr();
      const route = `dashboard/${spaceId}/messages/${conversation.objectId}`;
      const updatedRoute = appendParamsToRoute(queryParamsStr, route);
      pushRoute(updatedRoute);
    }
  };

  const createStyles = (theme: any) => ({
    alert: {
      alignItems: 'flex-start',
      marginTop: 1,
      bgcolor:
        theme.palette.mode === 'dark'
          ? AULA_DARK_ALERT_INFO_BG_COLOR
          : AULA_LIGHT_ALERT_INFO_BG_COLOR,
      border: 'none',
      borderRadius: '0px',
    },
  });
  const styles = createStyles(useTheme());

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Text kind="h4">Create a conversation</Text>
        {onClose && (
          <IconButton
            aria-label="Close message popup"
            onClick={handleCloseOrPushToMessages}
          >
            <Icons.Close />
          </IconButton>
        )}
      </Box>
      <Box sx={{ marginTop: 2 }}>
        <Autocomplete
          sx={{ maxWidth: 'calc(100dvw - 32px)' }}
          multiple
          chipSize="small"
          limitRows={3}
          label="Participants"
          placeholder="Add one or more participants..."
          loading={loadingSearch}
          options={options || []}
          renderOption={(props, option) => (
            <li {...(props as any)} key={option.id}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar
                  src={option.avatar}
                  fallbackText={`${
                    option?.firstName
                      ? option.firstName.charAt(0).toUpperCase()
                      : 'U'
                  }`}
                  sx={{ width: 24, height: 24, fontSize: '12px' }}
                />
                <Text
                  sx={{
                    color: palette.text.primary,
                    fontSize: '16px',
                  }}
                >
                  {option.firstName} {option.lastName}
                </Text>
                {isUserInstructorInSpace(option.id, spaceId) && (
                  <Chip
                    label="Educator"
                    size="small"
                    sx={{
                      backgroundColor: isDarkMode
                        ? palette.secondary.main
                        : palette.info.main,
                      borderColor: isDarkMode
                        ? palette.secondary.main
                        : palette.info.main,
                      color: isDarkMode
                        ? palette.common.black
                        : palette.common.white,
                      fontSize: '12px',
                    }}
                  />
                )}
              </Box>
            </li>
          )}
          renderSelectedOption={(option: SelectedUser) => (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: 0,
                ml: '-5px',
              }}
            >
              <Avatar
                src={option.avatar}
                fallbackText={`${
                  option?.firstName
                    ? option.firstName.charAt(0).toUpperCase()
                    : 'U'
                }`}
                sx={{
                  width: 18,
                  height: 18,
                  fontSize: '10px',
                  paddingLeft: '0px',
                  paddingTop: '1px',
                  position: 'absolute',
                }}
              />
              <Text
                sx={{
                  fontSize: '13px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'block',
                  ml: '22px',
                }}
              >
                {option.firstName} {option.lastName}
              </Text>
              {isUserInstructorInSpace(option.id, spaceId) && (
                <Chip
                  label="Educator"
                  size="small"
                  sx={{
                    backgroundColor: isDarkMode
                      ? palette.secondary.main
                      : palette.info.main,
                    borderColor: isDarkMode
                      ? palette.secondary.main
                      : palette.info.main,
                    color: isDarkMode
                      ? palette.common.black
                      : palette.common.white,
                    fontSize: '8.9px',
                    maxHeight: '16.44px',
                    marginBottom: '2px',
                  }}
                />
              )}
            </Box>
          )}
          filterOptions={(options) =>
            options.filter(
              (o) =>
                o.id !== userId &&
                !conversationParticipants.map((c) => c.id).includes(o.id)
            )
          }
          noOptionsText={
            searchText === '' || options === null
              ? 'Type to search'
              : 'No matches found'
          }
          inputValue={searchText}
          onInputChange={(_, value) => {
            setSearchText(value);
            handleUserSearch(value);
          }}
          value={conversationParticipants}
          onChange={(_, newVals) =>
            handleConversationParticipantsChange(newVals || [])
          }
        />
        {conversationParticipants.length > 1 && (
          <>
            <TextField
              label="Conversation title"
              placeholder="Eg - Design Group.."
              value={conversationName}
              onChange={handleConversationNameChange}
            />
            {userIsInstructor && (
              <>
                <Text sx={{ fontSize: '16px', fontWeight: '500' }}>
                  If you want to create a group, go to the&nbsp;
                  <Link
                    sx={{
                      color: palette.primary.main,
                      cursor: 'pointer',
                      textDecoration: 'none',
                    }}
                    onClick={() => {
                      const messageId =
                        window.location.hash
                          .split('/messages/')[1]
                          ?.split('?')[0] || undefined;
                      pushRoute(
                        `dashboard/${spaceId}/people/groups?from=messages` +
                          (messageId ? `&msgId=${messageId}` : '')
                      );
                    }}
                  >
                    Groups
                  </Link>
                  &nbsp;page.
                </Text>
                <Alert
                  severity="info"
                  icon={<Icons.InfoOutlined />}
                  sx={styles.alert}
                >
                  Use groups to organise students into project teams, with the
                  option to assign them automatically or manually.
                </Alert>
              </>
            )}
          </>
        )}
      </Box>
      <Box
        sx={{
          marginTop: 2,
          display: 'flex',
          gap: 2,
          justifyContent: 'end',
        }}
      >
        <Button
          sx={{
            textTransform: 'none',
            '&:hover': {
              borderColor: palette.primary.dark,
            },
          }}
          kind="secondary"
          onClick={handleCloseOrPushToMessages}
        >
          Cancel
        </Button>
        <Button
          sx={{
            textTransform: 'none',
            '&:hover': {
              backgroundColor: palette.primary.dark,
            },
          }}
          onClick={handleCreateConversation}
          loading={loadingCreation}
          disabled={
            conversationParticipants.length < 1 ||
            (conversationParticipants.length > 1 && conversationName.length < 2)
          }
        >
          Next
        </Button>
      </Box>
    </>
  );
};

export default CreateConversation;
